import {
	createContext,
	useContext,
	useEffect,
	useLayoutEffect,
	useState
} from 'react'
import {
	DEFAULT_THEME,
	getInitialTheme,
	persistThemeCookie,
	type Theme
} from '#lib/theme'

// useLayoutEffect on client to re-apply the theme class synchronously after
// React's hydration reconciliation but before paint — closes the flash gap
// that opens when React strips the script-set className from <html> on the
// `theme: 'system'` path. Falls back to useEffect on server.
const useIsomorphicLayoutEffect =
	typeof window === 'undefined' ? useEffect : useLayoutEffect

interface ThemeProviderProps {
	children: React.ReactNode
	initialTheme?: Theme
}

interface ThemeProviderState {
	theme: Theme
	setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
	theme: 'system',
	setTheme: () => null
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
	children,
	initialTheme,
	...props
}: ThemeProviderProps) {
	const [theme, setTheme] = useState<Theme>(
		() => initialTheme ?? DEFAULT_THEME
	)

	// `initialTheme` comes from the router's dehydrated root context, which a
	// prerendered SPA shell bakes at build time with no request cookie — always
	// DEFAULT_THEME. Seeding from it is required for a hydration-safe first
	// render, but on the client the live cookie is the real source (the same one
	// the inline <head> script reads). Reconcile to it on mount in a layout
	// effect so the correction lands before paint, instead of the layout effect
	// below overwriting the script-set class with the stale value.
	useIsomorphicLayoutEffect(() => {
		setTheme((current) => {
			const cookieTheme = getInitialTheme()
			return cookieTheme === current ? current : cookieTheme
		})
	}, [])

	useIsomorphicLayoutEffect(() => {
		applyTheme(theme)
	}, [theme])

	useEffect(() => {
		if (theme !== 'system') {
			return
		}

		const media = window.matchMedia('(prefers-color-scheme: dark)')
		const handleChange = () => applyTheme('system')

		media.addEventListener('change', handleChange)
		return () => media.removeEventListener('change', handleChange)
	}, [theme])

	const value = {
		theme,
		setTheme: (nextTheme: Theme) => {
			persistThemeCookie(nextTheme)
			setTheme(nextTheme)
		}
	}

	return (
		<ThemeProviderContext.Provider {...props} value={value}>
			{children}
		</ThemeProviderContext.Provider>
	)
}

export const useTheme = () => {
	const context = useContext(ThemeProviderContext)

	if (context === undefined) {
		throw new Error('useTheme must be used within a ThemeProvider')
	}

	return context
}

function applyTheme(theme: Theme) {
	const root = window.document.documentElement
	const resolvedTheme = getResolvedTheme(theme)

	root.classList.remove('light', 'dark')
	root.classList.add(resolvedTheme)
	root.style.colorScheme = resolvedTheme
}

function getResolvedTheme(theme: Theme) {
	if (theme !== 'system') {
		return theme
	}

	return window.matchMedia('(prefers-color-scheme: dark)').matches
		? 'dark'
		: 'light'
}
